import { useCallback, useEffect, useRef, useState } from "react";

export const SEARCH_DEBOUNCE_MS = 300;

export function createDebouncedCommit<T>(commit: (value: T) => void) {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const cancel = () => {
    if (timer === undefined) return;
    globalThis.clearTimeout(timer);
    timer = undefined;
  };

  return {
    schedule(value: T, delayMs: number) {
      cancel();
      timer = globalThis.setTimeout(() => {
        timer = undefined;
        commit(value);
      }, delayMs);
    },
    cancel,
  };
}

/**
 * Keeps typing responsive: the field updates on every keystroke, but the committed
 * value that drives the URL and the request only changes once typing pauses.
 */
export function useDebouncedSearch(
  committed: string,
  commit: (value: string) => void,
  delayMs = SEARCH_DEBOUNCE_MS,
): [string, (value: string) => void, (value: string) => void] {
  const [draft, setDraft] = useState(committed);
  const commitRef = useRef(commit);
  const controllerRef = useRef<ReturnType<typeof createDebouncedCommit<string>> | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = createDebouncedCommit((value) => commitRef.current(value));
  }

  useEffect(() => {
    commitRef.current = commit;
  });

  useEffect(() => setDraft(committed), [committed]);

  useEffect(() => {
    const controller = controllerRef.current!;
    if (draft === committed) {
      controller.cancel();
      return;
    }
    controller.schedule(draft, delayMs);
    return controller.cancel;
  }, [draft, committed, delayMs]);

  useEffect(() => () => controllerRef.current?.cancel(), []);

  const resetDraft = useCallback((value: string) => {
    controllerRef.current?.cancel();
    setDraft(value);
  }, []);

  return [draft, setDraft, resetDraft];
}
